import { ColourVariant } from "@shared/ui.design-system";
import { CloseIcon } from "@shared/ui.icons";
import { Link, Typography } from "@shared/ui.primitives";

import {
  alertContent,
  alertHeader,
  alertWrapper,
  closeIcon,
  iconContainer
} from "./Alert.css";

export type AlertVariant = "info" | "error" | "warn" | "accent" | "success";

type BaseAlertProps = {
  body: string;
  dataTestId?: string;
  icon?: React.ReactNode;
  mode?: "light" | "dark";
  variant?: AlertVariant;
};

type TitleProps =
  // If title isn't defined, dismissible and dismissOnClick are not available as props
  | {
      title?: undefined;
      dismissible?: undefined;
      dismissOnClick?: undefined;
    }
  // If title is defined, dismissible and dismissOnClick are available as props
  | {
      title: string;
      dismissible?: boolean;
      dismissOnClick?: () => void;
    };

type LinkProps =
  // If linkText isn't defined, linkHref and linkOnClick are not available as props
  | {
      linkText?: undefined;
      linkHref?: undefined;
      linkOnClick?: undefined;
    }
  // If linkText is defined, linkHref and linkOnClick are available as props
  | {
      linkText: string;
      linkHref: string;
      linkOnClick?: () => void;
    };

export type AlertProps = BaseAlertProps & TitleProps & LinkProps;

const alertTypographyColours: Record<AlertVariant, ColourVariant> = {
  info: "info-dark",
  error: "error-dark",
  warn: "warning-dark",
  accent: "primary-dark",
  success: "success-dark"
};

/**
 * Renders an alert component with customizable appearance, title, body, and optional link and dismiss functionality.
 *
 * @remarks
 * The `Alert` component is used to display important messages to users, such as notifications, warnings, or errors.
 * It supports multiple variants for different alert types, optional titles, dismiss buttons, and action links.
 * The appearance can be toggled between light and dark modes.
 *
 * @param {AlertProps} props - The props for the Alert component.
 *
 * @param {string} props.body - The main message or content of the alert.
 *
 * @param {string} [props.dataTestId] - Optional test ID for testing purposes. If not provided, a default is generated.
 *
 * @param {React.ReactNode} [props.icon] - Optional icon to display alongside the alert message.
 *
 * @param {"light" | "dark"} [props.mode="light"] - The visual mode of the alert, either "light" or "dark".
 *
 * @param {"info" | "error" | "warn" | "accent" | "success"} [props.variant="info"] - The variant of the alert, which determines its color and icon.
 *
 * @param {string} [props.title] - Optional title for the alert. If provided, the alert can be dismissible.
 *
 * @param {boolean} [props.dismissible=false] - If true and a title is present, shows a dismiss button.
 *
 * @param {() => void} [props.dismissOnClick] - Callback function invoked when the dismiss button is clicked.
 *
 * @param {string} [props.linkText] - Optional text for an action link. If provided, `linkHref` must also be provided.
 *
 * @param {string} [props.linkHref] - The URL for the action link. Required if `linkText` is provided.
 *
 * @param {() => void} [props.linkOnClick] - Optional callback invoked when the action link is clicked.
 *
 * @returns {JSX.Element} The rendered alert component.
 *
 * @example
 * ```tsx
 * <Alert
 *   title="Warning"
 *   body="Your session will expire soon."
 *   variant="warn"
 *   dismissible
 *   dismissOnClick={() => setShowAlert(false)}
 *   linkText="Learn more"
 *   linkHref="/help/session"
 *   linkOnClick={() => trackHelpClick()}
 * />
 * ```
 */
export const Alert = ({
  body,
  dataTestId,
  dismissible = false,
  dismissOnClick,
  icon,
  linkHref,
  linkOnClick,
  linkText,
  mode = "light",
  title,
  variant = "info"
}: AlertProps): JSX.Element => {
  const textColour = alertTypographyColours[variant];

  const computedTestId = dataTestId || `alert-${variant}-${mode}`;

  return (
    <div
      className={alertWrapper({ variant, mode })}
      data-testid={computedTestId}
    >
      {icon && <div className={iconContainer({ variant })}>{icon}</div>}
      <div className={alertContent}>
        {title && (
          <div className={alertHeader}>
            <Typography colour={textColour} variant="subheader-7">
              {title}
            </Typography>
            {dismissible && (
              <CloseIcon
                color={textColour}
                data-testid={`${computedTestId}-close`}
                onClick={dismissOnClick}
                className={closeIcon}
              />
            )}
          </div>
        )}
        <Typography colour={textColour} variant="body-sm">
          {body}
        </Typography>
        {linkHref && linkText && (
          <Link
            href={linkHref}
            noPadding
            onClick={linkOnClick}
            size="small"
            text={linkText}
            textColour={textColour}
          />
        )}
      </div>
    </div>
  );
};
